// Semantic colour roles for the highlight visual layer.
//
// Marks never name a hex directly. They name a role, and the role resolves to a
// FuseGrid design token with a literal fallback for contexts that have no
// stylesheet (the exported HTML that becomes the PDF, and the vitest snapshots).
//
// The palette is the one the CFO chart kit already uses: teal for favorable,
// clay for unfavorable. Deliberately NOT red/green — the pair is unreadable for
// the ~8% of men with a red-green deficiency, and it is the single most common
// accessibility failure in financial reporting.
//
// Hue is never the only channel. Every mark that carries a direction also
// carries a sign in its label and a position relative to a baseline.

export type VisualColorRole =
  | 'favorable'
  | 'unfavorable'
  | 'attention'
  | 'anchor'
  | 'brand'
  | 'ink'
  | 'ink-secondary'
  | 'muted'
  | 'grid'
  | 'axis'
  | 'surface'
  | 'band-favorable'
  | 'band-neutral'

const ROLES: Readonly<Record<VisualColorRole, { token: string; fallback: string }>> = {
  favorable: { token: '--fgvis-favorable', fallback: '#0e7c86' },
  unfavorable: { token: '--fgvis-unfavorable', fallback: '#c0492f' },
  attention: { token: '--fgvis-attention', fallback: '#b7791f' },
  anchor: { token: '--fgvis-anchor', fallback: '#9aa0a8' },
  brand: { token: '--fgvis-brand', fallback: '#1b4965' },
  ink: { token: '--fgvis-ink', fallback: '#1c1f26' },
  'ink-secondary': { token: '--fgvis-ink-2', fallback: '#4c5360' },
  muted: { token: '--fgvis-muted', fallback: '#868c9a' },
  grid: { token: '--fgvis-grid', fallback: '#ece8de' },
  axis: { token: '--fgvis-axis', fallback: '#3a3f47' },
  surface: { token: '--fgvis-surface', fallback: '#fffdf8' },
  'band-favorable': { token: '--fgvis-band-favorable', fallback: 'rgba(14,124,134,0.10)' },
  'band-neutral': { token: '--fgvis-band-neutral', fallback: 'rgba(154,160,168,0.14)' },
}

/** `var(--token, fallback)` so the mark themes where it can and still paints where it cannot. */
export const color = (role: VisualColorRole): string => `var(${ROLES[role].token}, ${ROLES[role].fallback})`

/** Literal hex, for contexts that cannot resolve CSS variables (e.g. PPTX). */
export const colorLiteral = (role: VisualColorRole): string => ROLES[role].fallback

/**
 * Direction → role, polarity-corrected.
 *
 * `favourable` is the effect on operating income, NOT the arithmetic sign. An
 * expense falling is a negative delta and a good outcome; rendering it red
 * because the number is negative is the bug this function exists to prevent.
 * Callers must pass the already-polarity-corrected boolean — see
 * `polarityOf()` in evidence.ts.
 */
export const directionRole = (favourable: boolean | null | undefined): VisualColorRole => {
  if (favourable == null) return 'anchor'
  return favourable ? 'favorable' : 'unfavorable'
}

/** Typography roles. Sizes are px in the SVG user-space of each scene. */
export const FONT = {
  family: "'FGVisualUI', Inter, Arial, sans-serif",
  value: { size: 10.5, weight: 600 },
  category: { size: 10.5, weight: 400 },
  axis: { size: 9.5, weight: 400 },
  annotation: { size: 10, weight: 500 },
} as const

/** Numeric labels use tabular figures so digit columns line up across rows. */
export const NUMERIC_TEXT_ATTRS = 'font-variant-numeric="tabular-nums" style="font-variant-numeric:tabular-nums"'

/**
 * `:root` declarations binding every `--fgvis-*` role to its literal value.
 *
 * The app gets these from `keyHighlights.css`, which maps them onto the CFO
 * design tokens. The exported board-report HTML has no stylesheet at all — it is
 * a self-contained document handed to headless Chrome — so it needs the same
 * roles declared inline, or every mark falls back to its hard-coded default and
 * the export silently stops tracking the theme.
 */
export const visualRoleCssText = (): string =>
  `:root{${Object.values(ROLES).map((role) => `${role.token}:${role.fallback};`).join('')}}`
